<!--
  This dialog will be used to fill the client's response for shopping popup.
-->
<template>
  <dialog-form
    :show-dialog="isShowDialog"
    :is-required="true"
    @open="dialogOpen"
    @cancel="hideDialog"
    @submit="submitForm"
  >
    <template #title>
      Tell the User about GoGo's Shopping Service!
    </template>

    <div class="row align-items-center">
      <div
        v-if="firstPlace"
        class="col-sm-12"
      >
        It looks like this client is going to or coming from <b>{{ firstPlace.name }} ({{ firstPlace.type }})</b>, please ask them if they'd
        be interested in more information about our GoGo's shopping service.
      </div>
      <div class="col-sm-12">
        Examples of nearby stores they can order from:
      </div>
      <div class="col-sm-12">
        <ul>
          <li
            v-for="(place, index) in placeList"
            :key="index"
          >
            {{ place.name }} ({{ place.type }})
          </li>
        </ul>
      </div>
    </div>

    <div class="row">
      <div class="col-12">
        <label class="control-label"> Client's Response* </label>
        <dropdown-list
          v-model="shoppingPopupResponse.clientResponse"
          placeholder="Please choose client's response"
          required
          :options="clientResponseList"
        />
      </div>
    </div>

    <template v-if="isNotInterested">
      <div class="row">
        <div class="col-12">
          <label class="control-label"> Reason* </label>
          <dropdown-list
            v-model="shoppingPopupResponse.notInterestedReason"
            required
            :options="notInterestedReasonList"
          />
        </div>
      </div>

      <div
        v-if="isNotInterestedOtherReason"
        class="row"
      >
        <div class="col-12">
          <label class="control-label"> Other Reason* </label>
          <sanitized-textarea
            v-model="shoppingPopupResponse.otherReason"
            required
          />
        </div>
      </div>
    </template>
  </dialog-form>
</template>

<script lang="ts">
import { ref, Ref, computed, PropType } from 'vue';
import _ from 'lodash';

import { ShoppingPlace } from '@/models/caller';

const dialogId = 'shopping-popup-dialog';

const clientResponseList = [
  { id: 1, key: 'Interested to learn more', label: 'Interested to learn more' },
  { id: 2, key: 'Not interested', label: 'Not interested' },
];
const notInterestedReasonList = [
  { id: 1, key: 'No Reason', label: 'No Reason' },
  { id: 2, key: 'Already know about it', label: 'Already know about it' },
  { id: 3, key: 'Prefers going to shopping store on their own', label: 'Prefers going to shopping store on their own' },
  { id: 4, key: 'Prefers ordering groceries themselves', label: 'Prefers ordering groceries themselves' },
  { id: 5, key: "Thinks it's too expensive", label: "Thinks it's too expensive" },
  { id: 6, key: 'Other Reason', label: 'Other Reason' },
];

export default {
  name: 'ShoppingPopupDialog',

  props: {
    isShowDialog: {
      type: Boolean,
      default: false,
    },
    shoppingPlaces: {
      type: Array as PropType<ShoppingPlace[]>,
      default: () => [],
    },
  },

  emits: ['submit', 'cancel'],

  setup: (props: any, context: any) => {
    const { emit } = context;

    const placeList = computed(() => {
      if (!props.shoppingPlaces) {
        return [];
      }

      return props.shoppingPlaces.map((place: ShoppingPlace) => ({
        name: place.name,
        type: _.upperFirst(_.lowerCase(place.type ?? '')),
      }));
    });

    const firstPlace = computed(() => {
      if (!placeList.value.length) {
        return null;
      }

      return placeList.value[0];
    });

    const shoppingPopupResponse: Ref<any> = ref({
      clientResponse: 'Interested to learn more',
      notInterestedReason: '',
      otherReason: '',
    });

    const isNotInterested = computed(() => {
      return shoppingPopupResponse.value.clientResponse === clientResponseList[1].key;
    });

    const isNotInterestedOtherReason = computed(() => {
      return shoppingPopupResponse.value.notInterestedReason === notInterestedReasonList[5].key;
    });

    const dialogOpen = () => {
      shoppingPopupResponse.value.clientResponse = 'Interested to learn more';
      shoppingPopupResponse.value.notInterestedReason = '';
      shoppingPopupResponse.value.otherReason = '';
    };

    const submitForm = () => {
      let notInterestedReason;
      if (isNotInterested.value && isNotInterestedOtherReason.value) {
        notInterestedReason = shoppingPopupResponse.value.otherReason;
      } else if (isNotInterested.value) {
        notInterestedReason = shoppingPopupResponse.value.notInterestedReason;
      }

      const postData: any = {
        clientResponse: shoppingPopupResponse.value.clientResponse,
        notInterestedReason,
      };

      emit('submit', postData);
    };

    const hideDialog = () => {
      emit('cancel');
    };

    return {
      dialogId,
      clientResponseList,
      notInterestedReasonList,
      isNotInterested,
      isNotInterestedOtherReason,
      shoppingPopupResponse,
      placeList,
      firstPlace,

      dialogOpen,
      submitForm,
      hideDialog,
    };
  },
};
</script>

<style lang="scss">
.grocery-popup-dialog {
  &.el-dialog {
    min-width: 700px !important;
  }
  .control-label {
    color: #252422 !important;
    font-weight: 500 !important;
  }
}
</style>
